/**
 * Algorithm Registry
 *
 * Singleton registry for managing prediction algorithms.
 * Allows dynamic registration and lookup of algorithms.
 */

import type { AlgorithmMetadata, PredictionAlgorithm } from "./types";

class AlgorithmRegistry {
  private algorithms = new Map<string, PredictionAlgorithm>();
  private defaultAlgorithmId: string | null = null;

  /**
   * Register a new algorithm.
   */
  register(algorithm: PredictionAlgorithm): void {
    this.algorithms.set(algorithm.metadata.id, algorithm);
  }

  /**
   * Get an algorithm by ID.
   */
  get(id: string): PredictionAlgorithm | undefined {
    return this.algorithms.get(id);
  }

  /**
   * Get all registered algorithms.
   */
  getAll(): PredictionAlgorithm[] {
    return Array.from(this.algorithms.values());
  }

  /**
   * Get all algorithm metadata for display.
   */
  getAllMetadata(): AlgorithmMetadata[] {
    return this.getAll().map((a) => a.metadata);
  }

  /**
   * Set the default algorithm.
   */
  setDefault(id: string): void {
    if (!this.algorithms.has(id)) {
      throw new Error(`Algorithm "${id}" not found`);
    }
    this.defaultAlgorithmId = id;
  }

  /**
   * Get the default algorithm.
   */
  getDefault(): PredictionAlgorithm | undefined {
    if (this.defaultAlgorithmId) {
      return this.algorithms.get(this.defaultAlgorithmId);
    }
    // Return first registered algorithm if no default set
    const first = this.algorithms.values().next().value;
    return first;
  }

  /**
   * Get the default algorithm ID.
   */
  getDefaultId(): string {
    return this.defaultAlgorithmId ?? this.getAll()[0]?.metadata.id ?? "";
  }

  /**
   * Check if an algorithm is registered.
   */
  has(id: string): boolean {
    return this.algorithms.has(id);
  }

  /**
   * Clear all registered algorithms.
   * Useful for testing.
   */
  clear(): void {
    this.algorithms.clear();
    this.defaultAlgorithmId = null;
  }
}

// Export singleton instance
export const algorithmRegistry = new AlgorithmRegistry();
